import { useEffect, useRef, useState } from 'react';
import { CircularProgress, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import { toast } from 'react-toastify';
import api from '../../utils/axiosConfig';
import { links } from '../../platform/links';
import { isSmartConnectUnit } from '../../utils/smartConnect';

// Mounted only while More is open, within navigation's account/unit scope.
export default function CallDeviceAction({ unit, onClose }) {
    const [loading, setLoading] = useState(false);
    const pending = useRef(null);
    useEffect(() => () => pending.current?.abort(), []);
    const call = async () => {
        if (pending.current || !isSmartConnectUnit(unit) || !unit?.UnitSerNum || !unit?.CustomerKey) return;
        const controller = new AbortController();
        pending.current = controller;
        setLoading(true);
        try {
            const { data } = await api.post('/api/unitData', {}, {
                params: { unit: unit.UnitSerNum }, signal: controller.signal
            });
            if (controller.signal.aborted) return;
            if (data?.userMap?.unit !== unit.UnitSerNum ||
                (data.unitStatus?.UnitSerNum && data.unitStatus.UnitSerNum !== unit.UnitSerNum)) {
                throw new Error('Device response does not match selection');
            }
            const number = data.numbersToBeAwareOf?.unitPhoneNumber;
            if (!number) {
                toast.info('No phone number is available for this device.');
                return;
            }
            await links.callPhone(number);
            if (!controller.signal.aborted) onClose();
        } catch {
            if (!controller.signal.aborted) toast.error('Unable to open the device call. Please try again.');
        } finally {
            if (!controller.signal.aborted) {
                pending.current = null;
                setLoading(false);
            }
        }
    };
    if (!isSmartConnectUnit(unit)) return null;
    return <ListItem disablePadding>
        <ListItemButton component="button" disabled={loading || !unit?.UnitSerNum || !unit?.CustomerKey}
            onClick={call} aria-label="Call Device" aria-busy={loading}
            sx={{ width: '100%', minHeight: 60, borderRadius: 2, border: 1, borderColor: 'divider', px: 1.5,
                whiteSpace: 'normal', overflowWrap: 'anywhere',
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
            <ListItemIcon sx={{ minWidth: '36px !important', color: 'primary.main' }}>
                {loading ? <CircularProgress size={24} aria-label="Getting device number" /> : <CallIcon />}
            </ListItemIcon>
            <ListItemText primary="Call Device" secondary={loading ? 'Getting device number…' : undefined}
                primaryTypographyProps={{ fontWeight: 600 }} sx={{ minWidth: 0 }} />
        </ListItemButton>
    </ListItem>;
}
